import { Box, Typography } from '@mui/material';

import { getFontFamilyCSS, getFontSizePixels } from 'config/signatureFonts';
import type { AgentSignature, SignatureSize } from 'types';

interface SignaturePreviewProps {
    signature: Partial<AgentSignature> & {
        typed_text?: string;
        font_family?: string;
        color?: string;
        size?: SignatureSize;
        creation_method?: AgentSignature['creation_method'];
        drawn_svg_data?: string;
        uploaded_image_base64?: string;
        uploaded_image_mime_type?: string;
        title?: string;
    };
    /** Show professional title under the signature */
    showTitle?: boolean;
    maxHeight?: number | string;
}

/**
 * Drawn signatures are often canvas PNGs embedded in an SVG with fixed
 * width/height and no viewBox. Without a viewBox, CSS max-size cannot scale
 * the nested <image>, so it overflows the preview. This adds a viewBox and
 * lets the SVG fill its container while preserving aspect ratio.
 */
function normalizeDrawnSvg(svgHtml: string): string {
    if (!svgHtml) {
        return svgHtml;
    }

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgHtml, 'image/svg+xml');
        const svg = doc.querySelector('svg');
        if (!svg) {
            return svgHtml;
        }

        const rawWidth = svg.getAttribute('width') || '';
        const rawHeight = svg.getAttribute('height') || '';
        const width = Number.parseFloat(rawWidth);
        const height = Number.parseFloat(rawHeight);

        if (
            !svg.getAttribute('viewBox') &&
            Number.isFinite(width) &&
            Number.isFinite(height) &&
            width > 0 &&
            height > 0
        ) {
            svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }

        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        // Drop intrinsic pixel size so CSS can constrain the element
        svg.removeAttribute('width');
        svg.removeAttribute('height');

        return new XMLSerializer().serializeToString(svg);
    } catch {
        return svgHtml;
    }
}

const previewFrameSx = (maxHeight: number | string) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '100%',
    height: maxHeight,
    maxHeight,
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
    // Drawn/uploaded assets often ship with a solid white canvas fill.
    // Multiply blends that white into the parent so previews match typed text.
    bgcolor: 'transparent',
});

/**
 * Renders a signature preview for typed, drawn, or uploaded methods.
 */
export function SignaturePreview({
    signature,
    showTitle = false,
    maxHeight = 120,
}: SignaturePreviewProps) {
    const method = signature.creation_method;

    if (method === 'typed' || (signature.typed_text && !method)) {
        if (!signature.typed_text) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Type your name to see a preview
                </Typography>
            );
        }

        return (
            <Box sx={{ textAlign: 'center', width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                <Box
                    component="div"
                    sx={{
                        fontFamily: getFontFamilyCSS(signature.font_family || 'Allura'),
                        fontSize: getFontSizePixels(signature.size || 'medium'),
                        color: signature.color || '#000000',
                        lineHeight: 1.2,
                        wordBreak: 'break-word',
                    }}
                >
                    {signature.typed_text}
                </Box>
                {showTitle && signature.title ? (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block' }}
                    >
                        {signature.title}
                    </Typography>
                ) : null}
            </Box>
        );
    }

    if (method === 'drawn' && signature.drawn_svg_data) {
        return (
            <Box
                sx={{
                    ...previewFrameSx(maxHeight),
                    // Soften solid white PNG fill baked into canvas exports
                    mixBlendMode: 'multiply',
                    '& svg': {
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        background: 'transparent',
                    },
                    '& image, & img': {
                        background: 'transparent',
                    },
                }}
                dangerouslySetInnerHTML={{
                    __html: normalizeDrawnSvg(signature.drawn_svg_data),
                }}
            />
        );
    }

    if (method === 'uploaded' && signature.uploaded_image_base64) {
        return (
            <Box sx={previewFrameSx(maxHeight)}>
                <Box
                    component="img"
                    src={`data:${signature.uploaded_image_mime_type};base64,${signature.uploaded_image_base64}`}
                    alt="Signature"
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        // Match typed look when the upload has a white background
                        mixBlendMode: 'multiply',
                        bgcolor: 'transparent',
                    }}
                />
            </Box>
        );
    }

    return (
        <Typography variant="body2" color="text.secondary">
            No preview available
        </Typography>
    );
}

export default SignaturePreview;
